import { ErrorAlert, LoadingAlert, Section, Tray } from '@/components';
import { useRoadmap } from '@/components/providers/postauth/RoadmapProvider';
import APP_CONFIG from '@/config/app';
import { FC } from 'react';
import { TbFlag3Filled } from 'react-icons/tb';
const formatEtaTimestamp = (eta: any): string => {
  if (!eta) {
    return 'Coming Soon';
  }
  try {
    if (eta && typeof eta.toDate === 'function') {
      const date = eta.toDate();
      if (!(date instanceof Date) || isNaN(date.getTime())) {
        console.error('Invalid date object from timestamp:', eta);
        return 'Coming Soon';
      }
      const month = date.getUTCMonth();
      const year = date.getUTCFullYear();
      let quarter;
      if (month <= 2) quarter = 1;
      else if (month <= 5) quarter = 2;
      else if (month <= 8) quarter = 3;
      else quarter = 4;
      return `Q${quarter} ${year}`;
    }
    if (
      eta &&
      typeof eta === 'object' &&
      'seconds' in eta &&
      'nanoseconds' in eta
    ) {
      const milliseconds = eta.seconds * 1000;
      const date = new Date(milliseconds);
      if (isNaN(date.getTime())) {
        console.error('Invalid date from seconds/nanoseconds:', eta);
        return 'Coming Soon';
      }
      const month = date.getUTCMonth();
      const year = date.getUTCFullYear();
      let quarter;
      if (month <= 2) quarter = 1;
      else if (month <= 5) quarter = 2;
      else if (month <= 8) quarter = 3;
      else quarter = 4;
      return `Q${quarter} ${year}`;
    }
    if (typeof eta === 'string' && /^Q[1-4]\s\d{4}$/.test(eta)) {
      return eta;
    }
    if (eta instanceof Date) {
      if (isNaN(eta.getTime())) {
        console.error('Invalid Date object:', eta);
        return 'Coming Soon';
      }
      const month = eta.getUTCMonth();
      const year = eta.getUTCFullYear();
      let quarter;
      if (month <= 2) quarter = 1;
      else if (month <= 5) quarter = 2;
      else if (month <= 8) quarter = 3;
      else quarter = 4;
      return `Q${quarter} ${year}`;
    }
    console.warn('Unexpected eta format:', eta);
    return 'Coming Soon';
  } catch (e) {
    console.error('Error formatting ETA timestamp:', e);
    return 'Coming Soon';
  }
};
const FlagIcon = TbFlag3Filled as React.ComponentType<any>;
const flagIconStyle = {
  width: '20px',
  height: '20px',
  minWidth: '20px',
  minHeight: '20px',
  maxWidth: '20px',
  maxHeight: '20px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
interface RoadmapTrayProps {
  isOpen: boolean;
  onClose: () => void;
}
const visualizeElementsAndSpacing = false;
const borderClassToVisualizeElementsAndSpacing = visualizeElementsAndSpacing
  ? 'border-white border'
  : '';
const spacingConfig = {
  gap: `gap${APP_CONFIG.STYLES.MAIN_GAP}`,
};
const roundednessConfig = {
  roundedness: `rounded${APP_CONFIG.STYLES.MAIN_ROUNDEDNESS}`,
};
const colorConfig = {
  backgroundColor: 'bg-primary/10',
  borderColor: 'border border-primary/10',
};
const RoadmapTray: FC<RoadmapTrayProps> = ({ isOpen, onClose }) => {
  const { roadmapItems, isLoading, error } = useRoadmap();
  if (isLoading) {
    return (
      <Tray
        isOpen={isOpen}
        onClose={onClose}
        showHeaderCloseButton={true}
        screens={[
          {
            title: 'Roadmap',
            content: (
              <LoadingAlert
                title="Roadmap items are being fetched"
                description="We're currently fetching roadmap items. This will only take a moment."
              />
            ),
          },
        ]}
      />
    );
  }
  if (error) {
    return (
      <Tray
        isOpen={isOpen}
        onClose={onClose}
        showHeaderCloseButton={true}
        screens={[
          {
            title: 'Roadmap',
            content: (
              <ErrorAlert
                title="Temporary glitch showing roadmap."
                description={`We're experiencing a temporary glitch showing the roadmap. Try refreshing the page, or please check back again in a few minutes. Error: ${
                  error?.message || 'Unknown error'
                }`}
              />
            ),
          },
        ]}
      />
    );
  }
  if (roadmapItems.length === 0) {
    return (
      <Tray
        isOpen={isOpen}
        onClose={onClose}
        showHeaderCloseButton={true}
        screens={[
          {
            title: 'Roadmap',
            content: (
              <ErrorAlert
                title="No roadmap found."
                description="There is currently no roadmap to show. Please check back later."
                icon="info"
              />
            ),
          },
        ]}
      />
    );
  }
  return (
    <Tray
      isOpen={isOpen}
      onClose={onClose}
      showHeaderCloseButton={true}
      screens={[
        {
          title: 'Roadmap',
          content: (
            <div className={`flex flex-col ${spacingConfig.gap}`}>
              {roadmapItems.map((item: any) => {
                const subHeaderText = formatEtaTimestamp(item.eta);
                return (
                  <Section
                    headerTitle={item.title}
                    subHeader={subHeaderText}
                    key={item.id}
                    body={item.description}
                    className={`${colorConfig.backgroundColor} 
                      ${roundednessConfig.roundedness} 
                      ${borderClassToVisualizeElementsAndSpacing}`}
                    icon={
                      <div style={flagIconStyle}>
                        <FlagIcon size={24} className={`text-accent`} />
                      </div>
                    }
                  />
                );
              })}
            </div>
          ),
        },
      ]}
    />
  );
};
export default RoadmapTray;
